/**
 * Shared chrome for the drawing-tool surfaces: the per-group icons, the
 * mode-control icons and the hide-mode labels. The pencil COMPONENT lives in
 * `PencilIcon.tsx`, since mixing it in here would break Fast Refresh.
 *
 * Two surfaces render the same catalog now - the floating `DrawingBar` on the
 * chart's left edge and the `DrawingToolsMenu` dropdown in the top toolbar - so
 * these constants live in one place rather than being duplicated (and drifting)
 * in each. Kept in a companion module, not in `DrawingBar.tsx`, because a
 * `.tsx` that mixes component and constant exports breaks Vite Fast Refresh.
 * Same filing as `toolIcons.tsx`.
 */
import { ToolIcons } from './toolIcons'

export const GroupIcons: Record<string, React.ReactNode> = {
  Lines: ToolIcons.straightLine,
  Channels: ToolIcons.parallelStraightLine,
  Position: ToolIcons.longPositionTool,
  Measure: ToolIcons.measure,
  Arrows: ToolIcons.arrow,
  Shapes: ToolIcons.rect,
}

export const ControlIcons = {
  ruler: (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" />
      <path d="m14.5 12.5 2-2" />
      <path d="m11.5 9.5 2-2" />
      <path d="m8.5 6.5 2-2" />
      <path d="m17.5 15.5 2-2" />
    </svg>
  ),
  // Horseshoe magnet, poles down. The previous glyphs were a pair of
  // checkmarks and a bullseye - neither read as "magnet" at 22px.
  magnetOff: (
    <svg
      width={22}
      height={22}
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M5.5 17V11a5.5 5.5 0 0111 0v6h-3.6v-6a1.9 1.9 0 00-3.8 0v6z" />
      {/* Pole bands - what makes it read as a magnet rather than an arch. */}
      <path d="M5.5 14.4h3.6M12.9 14.4h3.6" />
    </svg>
  ),
  magnetOn: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <path d="M5.5 17V11a5.5 5.5 0 0111 0v6h-3.6v-6a1.9 1.9 0 00-3.8 0v6z" />
    </svg>
  ),
  lock: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <path d="M15.5 10h-.75V7.75a3.75 3.75 0 10-7.5 0V10H6.5a1.5 1.5 0 00-1.5 1.5v5A1.5 1.5 0 006.5 18h9a1.5 1.5 0 001.5-1.5v-5a1.5 1.5 0 00-1.5-1.5zM8.75 7.75a2.25 2.25 0 014.5 0V10h-4.5V7.75zM15.5 16.5h-9v-5h9v5z" />
    </svg>
  ),
  unlock: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <path d="M15.5 10h-6.25V7.75a1.75 1.75 0 013.5 0 .75.75 0 001.5 0 3.25 3.25 0 10-6.5 0V10H6.5a1.5 1.5 0 00-1.5 1.5v5A1.5 1.5 0 006.5 18h9a1.5 1.5 0 001.5-1.5v-5a1.5 1.5 0 00-1.5-1.5zm0 6.5h-9v-5h9v5z" />
    </svg>
  ),
  visible: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <path d="M11 5C6.455 5 2.573 7.91 1 12c1.573 4.09 5.455 7 10 7s8.427-2.91 10-7c-1.573-4.09-5.455-7-10-7zm0 11.667A4.672 4.672 0 016.333 12 4.672 4.672 0 0111 7.333 4.672 4.672 0 0115.667 12 4.672 4.672 0 0111 16.667zM11 9a3 3 0 100 6 3 3 0 000-6z" />
    </svg>
  ),
  invisible: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <path d="M11 7.333c2.577 0 4.667 2.09 4.667 4.667 0 .607-.127 1.18-.34 1.713l2.726 2.727A11.807 11.807 0 0021 12c-1.573-4.09-5.455-7-10-7-.873 0-1.72.107-2.533.3l2.02 2.02c.533-.213 1.106-.34 1.713-.34v.353zM2.333 3.693l2.127 2.127.427.427A11.774 11.774 0 001 12c1.573 4.09 5.455 7 10 7 1.013 0 1.993-.16 2.913-.447l.393.394 2.727 2.72.94-.94L3.273 2.753l-.94.94zM7.66 9.02l1.147 1.147A2.649 2.649 0 008.667 11 2.336 2.336 0 0011 13.333c.28 0 .56-.047.82-.14l1.147 1.147A4.633 4.633 0 0111 14.667 4.672 4.672 0 016.333 10c0-.493.1-.96.267-1.4l1.06 1.06V9.02z" />
    </svg>
  ),
  remove: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <path d="M16.967 8.671a.543.543 0 00-.542.547v7.413c0 .691-.418 1.276-.911 1.276H7.486c-.493 0-.911-.585-.911-1.276V9.218a.543.543 0 00-1.084 0v7.413C5.491 17.938 6.386 19 7.486 19h8.026c1.1 0 1.997-1.062 1.997-2.369V9.218a.543.543 0 00-.542-.547zM18.458 6.212H4.542a.543.543 0 000 1.093h13.916a.543.543 0 000-1.093zM8.681 5.1h5.638a.543.543 0 000-1.1H8.681a.543.543 0 000 1.1z" />
      <path d="M10.152 9.218v8.689a.543.543 0 001.084 0V9.218a.543.543 0 00-1.084 0zM12.846 9.218v8.689a.543.543 0 001.085 0V9.218a.543.543 0 00-1.085 0z" />
    </svg>
  ),
}

/* ------------------------------------------------------------------ */
/*  Hide modes (eye button)                                            */
/* ------------------------------------------------------------------ */

export type HideMode = 'drawings' | 'indicators' | 'both'

export const HIDE_MODE_LABELS: Record<HideMode, string> = {
  drawings: 'drawings',
  indicators: 'indicators',
  both: 'drawings & indicators',
}
